import { forwardRef, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { CFormGroup, CLabel } from "@coreui/react";

import { deXuatNhaCungCapApi } from "src/1/apis/de_xuat_nha_cung_cap.api";
import { CModal } from "src/1/common/components/others";
import { CInput, CRating, CSelectMulti } from "src/common/components/controls";

import { FilesCell } from "./FilesCell";

const defaultValues = {
  code: "",
  name: "",
  financial: 5,
  reputation: 5,
  quality: 5,
  pricing: 5,
  materials: [],
  files: [],
};

export const FormModal = forwardRef(
  ({ formId, materials_options = [], refetch }, ref) => {
    //#region Data
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("create"); // "create" | "edit"

    const {
      control,
      handleSubmit,
      reset,
      formState: { isSubmitting },
    } = useForm({ defaultValues });
    //#endregion

    //#region Event
    const onClose = () => {
      setOpen(false);
      setMode("create");
      reset(defaultValues);
    };

    const onListChange = (changeCb) => (options) => {
      changeCb(options?.map((opt) => opt?.value));
    };

    const onSubmit = handleSubmit(async (values) => {
      try {
        const payload = { ...values, files: values?.files?.map((e) => e.id) };
        if (mode === "create") {
          await deXuatNhaCungCapApi.addSupplier(formId, payload);
        } else {
          const { id } = values;
          await deXuatNhaCungCapApi.updateSupplier(formId, id, payload);
        }
        toast.success("Lưu thông tin nhà cung cấp thành công!");
        refetch?.();
        onClose();
      } catch (error) {
        toast.error(
          error?.message ?? "Lưu thông tin nhà cung cấp không thành công!",
        );
      }
    });
    //#endregion

    useImperativeHandle(ref, () => ({
      open: (editData) => {
        if (editData) {
          setMode("edit");
          reset({
            id: editData?.id,
            code: editData?.code,
            name: editData?.name,
            financial: editData?.financial,
            reputation: editData?.reputation,
            quality: editData?.quality,
            pricing: editData?.pricing,
            materials: editData?.materials?.map((e) => e?.code),
            files: editData?.files,
          });
        }
        setOpen(true);
      },
    }));

    //#region Render
    return (
      <CModal
        open={open}
        onClose={onClose}
        title={`${mode === "create" ? "Thêm" : "Sửa"} nhà cung cấp`}
        loading={isSubmitting}
        onSubmit={onSubmit}
      >
        <div className="grid grid-cols-4 gap-5">
          <Controller
            control={control}
            name="code"
            render={({ field }) => (
              <CInput
                {...field}
                placeholder="Mã NCC hệ thống tự tạo"
                label="Mã NCC"
                required
                className="col-span-2"
                disabled
              />
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <CInput
                {...field}
                placeholder="Nhập tên NCC"
                label="Tên NCC"
                required
                className="col-span-2"
              />
            )}
          />
          <Controller
            control={control}
            name="financial"
            render={({ field }) => (
              <CFormGroup>
                <CLabel>Năng lực tài chính:</CLabel>
                <CRating value={field.value} onChange={field.onChange} />
              </CFormGroup>
            )}
          />
          <Controller
            control={control}
            name="reputation"
            render={({ field }) => (
              <CFormGroup>
                <CLabel>Uy tín NCC:</CLabel>
                <CRating value={field.value} onChange={field.onChange} />
              </CFormGroup>
            )}
          />
          <Controller
            control={control}
            name="quality"
            render={({ field }) => (
              <CFormGroup>
                <CLabel>Chất lượng:</CLabel>
                <CRating value={field.value} onChange={field.onChange} />
              </CFormGroup>
            )}
          />
          <Controller
            control={control}
            name="pricing"
            render={({ field }) => (
              <CFormGroup>
                <CLabel>Giá cả:</CLabel>
                <CRating value={field.value} onChange={field.onChange} />
              </CFormGroup>
            )}
          />
          <Controller
            control={control}
            name="materials"
            render={({ field }) => (
              <CFormGroup className="col-span-2">
                <CLabel>Danh sách NVL cung cấp:</CLabel>
                <CSelectMulti
                  {...field}
                  onChange={onListChange(field.onChange)}
                  options={materials_options}
                  canWrap
                />
              </CFormGroup>
            )}
          />
          <Controller
            control={control}
            name="files"
            render={() => (
              <CFormGroup className="col-span-2">
                <CLabel>Tài liệu minh chứng:</CLabel>
                <FilesCell control={control} />
              </CFormGroup>
            )}
          />
        </div>
      </CModal>
    );
    //#endregion
  },
);
